import React from 'react'
import NetWiseClient from 'api/client'
import { useOktaAuth } from '@okta/okta-react'
import { SocketHandler } from 'api/TaskSocket'
import NetwiseSocket from 'api/NetwiseSocket'
import tm from 'analytics/TagManager'
import Client from 'api/client'
import Worker from 'context/auth-context/worker'
import endpoints from 'api/endpoints'
import { useSnackbar } from './snackbar-context'
import { Context } from 'react'
import { useAlerts } from './alert-context'
import { Task, SnackbarMessage, UserData } from 'types'

const contextWorker = Worker()
const client = new Client(contextWorker)
const AuthContext: Context<any> = React.createContext(null)
const socketHandler = new SocketHandler()
let netwiseSocket: NetwiseSocket

function AuthProvider(props: any) {
  const { oktaAuth, authState } = useOktaAuth()

  const [userData, setUserData] = React.useState<UserData | null>(null)
  const [allDataMode, setAllDataMode] = React.useState(false)
  const [adminMode, setAdminMode] = React.useState(false)
  const [buildVersion, setBuildVersion] = React.useState()
  const { sendAlert } = useAlerts()
  const { setSnackbarMessage: setMessage } = useSnackbar()

  const sessionsUrl = client.endpoints.sessions
  client.showAlertMessage = sendAlert
  socketHandler.setNotificationFunction(setMessage)

  // Setting up NetwiseSocket connection
  if (!netwiseSocket) {
    netwiseSocket = new NetwiseSocket({
      endpoint: 'check_tasks/?auth-context',
      setNotification: setMessage,
    })
  }

  // New logout function for Okta
  const logout = React.useCallback(async () => {
    await client.worker.setJwt(null)
    await oktaAuth.signOut()
  }, [oktaAuth])

  const loginWithOkta = React.useCallback(
    async (oktaToken: string) => {
      // This function exchanges the Okta token for the application's JWT.
      // The backend is expected to have an endpoint (e.g., POST /api/sessions/)
      // that accepts the `okta_access_token`, validates it, and returns
      // the application's own JWT if the user is valid.
      const { responseData } = await client.post({
        endpoint: 'okta-sessions',
        data: { okta_access_token: oktaToken },
        auth: false, // This call is for authentication, so it doesn't send an existing app JWT.
      })

      const appToken = responseData?.access

      if (appToken) {
        await client.worker.setJwt(appToken)
      } else {
        // This case handles a successful (2xx) response that is missing the access token.
        throw new Error(
          responseData?.error || 'Login failed: No application token was received.'
        )
      }
    },
    [sessionsUrl]
  )

  // This function is now simplified. It assumes the client is already authenticated
  // via the Okta access token and fetches the current user's data from the backend.
  const getUserData = React.useCallback(async (): Promise<UserData | null> => {
    // We need an endpoint to get the current user's data without knowing their ID.
    // This endpoint will rely on the Bearer token to identify the user.
    const meEndpoint = client.endpoints.usersAPI + '/me'

    const [{ responseData: r1 }, { responseData: r2 }] = await Promise.all([
      client.get({ endpoint: meEndpoint }),
      client.get({ endpoint: client.endpoints.sessions }),
    ])

    // The /me/ endpoint might return the user object directly, while list endpoints nest it in 'results'.
    // This handles both cases by checking for the 'results' key first.
    const newUserData = r1?.results || r1
    const modes = r2?.results || r2

    // If either API call failed to return the expected data, throw an error.
    if (!newUserData || !modes) {
      throw new Error('Failed to retrieve user or session data from the server.')
    }

    const { data_mode_all, admin_mode } = modes
    setAllDataMode(data_mode_all)
    setAdminMode(admin_mode)

    // TODO: DEPRECATED: must replace for PLAT-1023
    newUserData.needsUpgrade =
      newUserData?.account?.type === 'analytics' &&
      newUserData?.account?.credits === 0

    const isNetWiseAccount = newUserData?.account?.id === 1
    client.setIsNetWiseAccount(isNetWiseAccount)

    // The `assumer_id` was previously in the JWT. The backend should now include this
    // in the response from the `me/` endpoint if a user is being assumed.
    return { ...newUserData, isNetWiseAccount }
  }, [setAllDataMode, setAdminMode])

  // Get app version data
  React.useEffect(() => {
    async function getVersionData() {
      try {
        const url = 'version/get_build_version'
        const { responseData } = await client.get({
          endpoint: url,
          auth: false,
        })
        setBuildVersion(responseData?.build_version)
      } catch (e) {
        console.log('Could not get version data')
        console.log(e)
      }
    }
    getVersionData()
  }, [])

  const { isAuthenticated, isPending } = authState || {}
  const token = authState?.accessToken?.accessToken

  // This effect hook reacts to changes in Okta's authentication state.
  React.useEffect(() => {
    // authState is null on initial render, and isPending is true while it's being determined.
    // We wait until it's a settled object.
    if (isPending) {
      return
    }

    const handleAuthChange = async () => {
      if (isAuthenticated) {
        if (!token) {
          // This can be a transient state where isAuthenticated is true but the
          // token hasn't been populated yet. The effect will re-run once it's available
          // due to the `token` dependency.
          // This can be a transient state. The effect will run again once the token is available.
          return
        }

        try {
          await loginWithOkta(token)
          const newUserData = await getUserData()
          setUserData(newUserData)
          if (newUserData) tm.captureLogin(newUserData)
        } catch (error: any) {
          // Prioritize the specific error from the API response body (e.g., from a 403).
          // Our backend sends { "error": "message" }. The API client should attach this to the error.
          const apiErrorMessage = error?.data?.error
          const errorMessage =
            apiErrorMessage ||
            error?.message ||
            'Login failed: An unexpected error occurred.'
          console.error(
            `Login failed during user verification: ${errorMessage}`,
            error
          )
          sendAlert({ message: errorMessage })
          await client.worker.setJwt(null)
          await oktaAuth.signOut()
        }
      } else {
        // User is not authenticated
        await client.worker.setJwt(null)
        setUserData(null)
      }
    }
    handleAuthChange()
  }, [isAuthenticated, isPending, token, oktaAuth, getUserData, loginWithOkta])

  // Function toggle the All Data Mode in the UI
  async function toggleAllDataMode(): Promise<any> {
    const { responseData } = await client.post({
      endpoint: sessionsUrl,
      data: { data_mode_all: !allDataMode },
    })
    const { data_mode_all } = responseData.results
    setAllDataMode(data_mode_all)
  }

  // Function toggle the Admin Mode in the UI
  async function toggleAdminMode(): Promise<any> {
    const { responseData } = await client.post({
      endpoint: sessionsUrl,
      data: { admin_mode: !adminMode },
    })
    const { admin_mode } = responseData.results
    setAdminMode(admin_mode)
  }

  // Perform actions on a users behalf requires you to first "assume" their id
  // Must refresh the token after assuming the id.
  const assumeUser = async (id: number) => {
    try {
      // The `assume` endpoint returns a new access token for the assumed user.
      const { responseData } = await client.post({
        endpoint: endpoints.assumeUser,
        data: { user_id: id },
        useCache: false,
      })

      // We must capture this new token and tell our client to use it.
      const newAccessToken = responseData?.access
      if (newAccessToken) {
        await client.worker.setJwt(newAccessToken)
      } else {
        throw new Error('Assumption failed: Did not receive a new access token.')
      }

      // Now that the client is using the new token, fetch the user data.
      // The /api/users/me endpoint will now return the assumed user's data.
      const newUserData = await getUserData()
      setUserData(newUserData)
    } catch (e: any) {
      console.error('Could not assume user role at this time.', e)
      sendAlert({ message: e.message || 'Failed to assume user. Please try again.' })
    }
  }

  // Unassuming the user
  const unassumeUser = async () => {
    try {
      const { responseData } = await client.post({
        endpoint: endpoints.unassumeUser,
        data: {},
        useCache: false,
      })
      const newAccessToken = responseData?.access
      if (newAccessToken) {
        await client.worker.setJwt(newAccessToken)
      } else {
        throw new Error('Un-assumption failed: Did not receive a new access token.')
      }
      const newUserData = await getUserData()
      setUserData(newUserData)
    } catch (e: any) {
      console.error('Could not unassume user role at this time.', e)
      sendAlert({
        message: e.message || 'Failed to return to your account. Please try again.',
      })
    }
  }

  return (
    <AuthContext.Provider
      value={{
        client: client,
        buildVersion: buildVersion,
        assumeUser: assumeUser,
        unassumeUser: unassumeUser,
        userData: userData,
        allDataMode: allDataMode,
        toggleAllDataMode: toggleAllDataMode,
        adminMode: adminMode,
        toggleAdminMode: toggleAdminMode,
        // The app can check `!authState.isPending` to know when auth has been checked.
        hasAttemptedRefresh: !authState?.isPending,
        loggedIn: authState?.isAuthenticated,
        // Login is now handled by redirecting to Okta, so the function is removed.
        logout: logout,
        // Password reset is handled by Okta.
        addNotifierTask: (tasks: Task[]) => netwiseSocket.processTasks(tasks),
        socketHandler: socketHandler,
      }}
      {...props}
    />
  )
}

interface Auth {
  client: NetWiseClient
  buildVersion: string
  assumeUser: (id: number) => void
  unassumeUser: () => void
  user: any
  userData: UserData
  allDataMode: boolean
  toggleAllDataMode: () => void
  adminMode: boolean
  toggleAdminMode: () => void
  hasAttemptedRefresh: boolean
  loggedIn: boolean
  login: (email: string, password: string) => boolean
  logout: () => void
  requestReset: (email: string) => any
  resetPassword: (token: string, password: string) => boolean
  addNotifierTask: (message: SnackbarMessage) => void
  socketHandler: SocketHandler
}

const useAuth = (): Auth => React.useContext(AuthContext) as Auth

export { AuthProvider, useAuth }
